import { PlayerImage } from "@/components/PlayerImage";
import { Separator } from "@/components/ui/separator";
import type { WebUser } from "@/types/WebUser";
import { CheckIcon } from "@roman/shared/components/Icons";
import { AvatarIcon } from "@roman/shared/components/Icons/AvatarIcon";
import type { RoundDetails } from "@roman/shared/types/RoundDetails";
import type { ScheduledEntry } from "@roman/shared/types/Schedule";
import { cn, getColorStyleByRank } from "@roman/shared/utils";
import { useMemo, useState } from "react";

export const ScheduleUsersTab = ({
  round,
  entries,
  user,
  onClick,
}: {
  round: RoundDetails;
  entries: ScheduledEntry[];
  user: WebUser;
  onClick: (entry: ScheduledEntry) => void;
}) => {
  const [showOnlyMyTeam, setShowOnlyMyTeam] = useState(false);
  const displayEntries = showOnlyMyTeam ? entries.filter((entry) => entry.userId === user.id) : entries;
  const isEndedRound = useMemo(() => round.entryCloseTime < new Date().getTime(), [round.entryCloseTime]);

  return (
    <div>
      <ShowOnlyMyTeam
        onClick={() => setShowOnlyMyTeam((prev) => !prev)}
        className={showOnlyMyTeam ? "bg-secondary" : "bg-[#CBCBCB]"}
      />

      <div>
        <div className={cn("flex flex-col overflow-y-auto flex-grow")}>
          {displayEntries.map((entry, index) => {
            return (
              <UserEntry
                key={`${entry.id}-${index}`}
                user={user}
                index={index}
                entry={entry}
                onClick={onClick}
                round={round}
                isEndedRound={isEndedRound}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const ShowOnlyMyTeam = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className="items-center flex space-x-2 py-5" {...props}>
    <div
      className={cn("rounded-full p-1 w-[18px] h-[18px] flex items-center justify-center cursor-pointer", className)}
    >
      <CheckIcon className="text-white" />
    </div>

    <label
      htmlFor="player-my-team"
      className="text-[#5C5C5C] cursor-pointer text-xs font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 select-none"
    >
      自分のチームだけを表示
    </label>
  </div>
);

type UserEntryProps = {
  entry: ScheduledEntry;
  round: RoundDetails;
  index: number;
  isEndedRound: boolean;
  user: WebUser | null;
  onClick: (entry: ScheduledEntry) => void;
};
const UserEntry = ({ entry, round, index, isEndedRound, user, onClick }: UserEntryProps) => {
  const { selectedCaptains } = entry;
  const isOwnEntry = entry.userId === user?.id;

  const isCaptain = (playerId: string): boolean => {
    return selectedCaptains.captainPlayerId === playerId;
  };

  const isViceCaptain = (playerId: string): boolean => {
    if (selectedCaptains.type === "0002") {
      return selectedCaptains.viceCaptainPlayerId === playerId;
    }
    return false;
  };

  const selectedPlayers = useMemo(() => {
    if (!isOwnEntry && !isEndedRound) {
      return [];
    }

    const selectedPlayersMap = entry.selectedPlayers.reduce(
      (map, playerId) => {
        map[playerId] = true;
        return map;
      },
      {} as Record<string, boolean>,
    );

    const result = [];
    for (const i in round.players.homePlayers) {
      const homePlayer = round.players.homePlayers[i];
      const awayPlayer = round.players.awayPlayers[i];
      const selectPlayer = selectedPlayersMap[homePlayer.id] ? homePlayer : awayPlayer;

      if (selectPlayer) {
        result.push(selectPlayer);
      }
    }

    return result;
  }, [entry, round.players, isOwnEntry, isEndedRound]);

  const handleClickUserEntry = () => {
    if (entry.userId === user?.id || (entry.userId !== user?.id && isEndedRound)) {
      onClick(entry);
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className={cn("px-3 grid grid-cols-12 items-center w-full py-3 cursor-pointer", isOwnEntry && "bg-light-blue")}
        onClick={handleClickUserEntry}
      >
        <div className="col-start-1 self-center">
          <div className="flex items-center">
            <p className={cn("w-1/2 bg-clip-text text-transparent text-2xl", getColorStyleByRank(index + 1))}>
              {index + 1}
            </p>
          </div>
        </div>

        <div className="col-span-2 md:col-span-1 w-full">
          <Avatar photoUrl={entry.userPhotoUrl} />
        </div>

        <div className=" col-span-9 md:col-span-10 w-full self-start pl-2 flex-col">
          <div className="pl-1 flex items-center">
            {isOwnEntry && (
              <div
                className={cn(
                  "rounded-full p-1 w-[18px] h-[18px] flex items-center justify-center cursor-pointer bg-secondary mr-1",
                )}
              >
                <CheckIcon className="text-white" />
              </div>
            )}
            <p className="font-semibold text-base-">{entry.userName}</p>
          </div>
          <div>
            <div className="flex pl-2 pt-1.5 justify-start gap-2 overflow-x-scroll w-full hide-scrollbar">
              {selectedPlayers.map((player) => (
                <div className="rounded-md" key={player.id}>
                  <PlayerImage
                    url={player.imageUrl}
                    name={player.name}
                    isCaptain={isCaptain(player.id)}
                    isViceCaptain={isViceCaptain(player.id)}
                    className="w-[46px] h-[70px]"
                    badgeClassName="w-[20px] h-[10px]"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Separator orientation="horizontal" className="bg-[#ECECEC]" />
    </div>
  );
};

export const Avatar = ({
  photoUrl,
  className,
}: {
  photoUrl: string | undefined;
  className?: string;
}) => {
  return (
    <div className={cn("relative flex items-center justify-center", className)}>
      <div className="flex justify-center items-center rounded-full w-10 h-10 bg-[#01A4F9]">
        <AvatarIcon />
      </div>
      {photoUrl && (
        <img
          src={photoUrl}
          alt="profile"
          className="absolute h-full object-cover rounded-full"
          draggable={false}
          referrerPolicy="no-referrer"
        />
      )}
    </div>
  );
};
